import { useState, useId } from 'react';
import type { ICustomAlertPreference } from '../../types/api-caller';
import PillToggle from '../PillToggle';

interface AdminCustomAlertEditorProps {
  alert?: ICustomAlertPreference;
  mode: 'create' | 'edit';
  createDefaultName?: string;
  onSave: (alert: ICustomAlertPreference) => void;
  onCancel?: () => void;
}

const AdminCustomAlertEditor: React.FC<AdminCustomAlertEditorProps> = ({ alert, mode, createDefaultName, onSave, onCancel }) => {
  const [name, setName] = useState<string>(alert?.name ?? createDefaultName ?? '');
  const [frequency, setFrequency] = useState<string>((alert?.frequency ?? 'DAILY').toLowerCase());
  const [scoreIncreaseAlertEnabled, setScoreIncreaseAlertEnabled] = useState<boolean>(
    mode === 'create' ? true : alert?.increase_threshold != null
  );
  const [scoreDecreaseAlertEnabled, setScoreDecreaseAlertEnabled] = useState<boolean>(alert?.decrease_threshold != null);
  const [scoreIncreaseValue, setScoreIncreaseValue] = useState<string>(
    alert?.increase_threshold != null ? String(alert.increase_threshold) : '10'
  );
  const [scoreDecreaseValue, setScoreDecreaseValue] = useState<string>(
    alert?.decrease_threshold != null ? String(alert.decrease_threshold) : '10'
  );
  const [excludeLowArticles, setExcludeLowArticles] = useState<boolean>(alert?.exclude_low_articles ?? false);

  const increaseId = useId();
  const decreaseId = useId();
  const lowArticlesId = useId();

  function buildPayload(
    overrides: Partial<{
      name: string;
      frequency: string;
      scoreIncreaseAlertEnabled: boolean;
      scoreDecreaseAlertEnabled: boolean;
      scoreIncreaseValue: string;
      scoreDecreaseValue: string;
      excludeLowArticles: boolean;
    }> = {}
  ): ICustomAlertPreference {
    const nextName = overrides.name ?? name;
    const nextFrequency = overrides.frequency ?? frequency;
    const nextScoreIncreaseEnabled = overrides.scoreIncreaseAlertEnabled ?? scoreIncreaseAlertEnabled;
    const nextScoreDecreaseEnabled = overrides.scoreDecreaseAlertEnabled ?? scoreDecreaseAlertEnabled;
    const nextScoreIncreaseValue = overrides.scoreIncreaseValue ?? scoreIncreaseValue;
    const nextScoreDecreaseValue = overrides.scoreDecreaseValue ?? scoreDecreaseValue;
    const nextExcludeLowArticles = overrides.excludeLowArticles ?? excludeLowArticles;

    return {
      name: nextName,
      alert_id: 0,
      enabled: alert?.enabled ?? true,
      increase_threshold: nextScoreIncreaseEnabled ? Number(nextScoreIncreaseValue) : null,
      decrease_threshold: nextScoreDecreaseEnabled ? Number(nextScoreDecreaseValue) : null,
      frequency: nextFrequency.toUpperCase(),
      scope_type: 'PORTFOLIO',
      exclude_low_articles: nextExcludeLowArticles,
      list_ids: [],
    };
  }

  function emitChange(
    overrides: Partial<{
      name: string;
      frequency: string;
      scoreIncreaseAlertEnabled: boolean;
      scoreDecreaseAlertEnabled: boolean;
      scoreIncreaseValue: string;
      scoreDecreaseValue: string;
      excludeLowArticles: boolean;
    }> = {}
  ) {
    if (mode === 'edit') {
      onSave(buildPayload(overrides));
    }
  }

  function handleFrequencyChange(val: string) {
    setFrequency(val);
    emitChange({ frequency: val });
  }

  function handleScoreIncreaseEnabledChange(checked: boolean) {
    if (!checked && !scoreDecreaseAlertEnabled) return;
    setScoreIncreaseAlertEnabled(checked);
    emitChange({ scoreIncreaseAlertEnabled: checked });
  }

  function handleScoreDecreaseEnabledChange(checked: boolean) {
    if (!checked && !scoreIncreaseAlertEnabled) return;
    setScoreDecreaseAlertEnabled(checked);
    emitChange({ scoreDecreaseAlertEnabled: checked });
  }

  function handleScoreIncreaseValueChange(value: string) {
    if (Number(value) >= 0 && Number(value) <= 100) {
      setScoreIncreaseValue(value);
      emitChange({ scoreIncreaseValue: value });
    }
  }

  function handleScoreDecreaseValueChange(value: string) {
    if (Number(value) >= 0 && Number(value) <= 100) {
      setScoreDecreaseValue(value);
      emitChange({ scoreDecreaseValue: value });
    }
  }

  function handleExcludeLowArticlesChange(checked: boolean) {
    setExcludeLowArticles(checked);
    emitChange({ excludeLowArticles: checked });
  }

  function handleNameChange(value: string) {
    setName(value);
    emitChange({ name: value });
  }

  function handleSaveClick() {
    if (mode === 'create' && name.trim() && (scoreIncreaseAlertEnabled || scoreDecreaseAlertEnabled)) {
      onSave(buildPayload());
    }
  }

  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="flex flex-row gap-4 items-center">
        <input
          type="text"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Alert name"
          className="flex-1 border border-gray-300 rounded px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ow-blue"
        />
      </div>

      <div className="flex flex-col gap-2">
        <PillToggle
          options={[
            { label: 'Daily', value: 'daily' },
            { label: 'Weekly', value: 'weekly' },
          ]}
          selected={frequency.toLowerCase()}
          onChange={handleFrequencyChange}
        />

        <div className="flex gap-3 items-center">
          <input
            id={increaseId}
            type="checkbox"
            checked={scoreIncreaseAlertEnabled}
            onChange={(e) => handleScoreIncreaseEnabledChange(e.target.checked)}
            className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer"
          />
          <label htmlFor={increaseId} className="font-medium text-base text-black cursor-pointer self-center">
            if FSS score increases by
          </label>
          <input
            type="number"
            min={0}
            max={100}
            className="w-16 border border-gray-300 rounded px-2 py-1 text-base"
            value={scoreIncreaseValue}
            onChange={(e) => handleScoreIncreaseValueChange(e.target.value)}
            placeholder="10"
          />
          <span className="text-base text-black self-center">%</span>
        </div>

        <div className="flex gap-3 items-center">
          <input
            id={decreaseId}
            type="checkbox"
            checked={scoreDecreaseAlertEnabled}
            onChange={(e) => handleScoreDecreaseEnabledChange(e.target.checked)}
            className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer"
          />
          <label htmlFor={decreaseId} className="font-medium text-base text-black cursor-pointer self-center">
            if FSS score decreases by
          </label>
          <input
            type="number"
            min={0}
            max={100}
            className="w-16 border border-gray-300 rounded px-2 py-1 text-base"
            value={scoreDecreaseValue}
            onChange={(e) => handleScoreDecreaseValueChange(e.target.value)}
            placeholder="10"
          />
          <span className="text-base text-black self-center">%</span>
        </div>

        <div className="grid grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-3 items-center">
          <input
            id={lowArticlesId}
            type="checkbox"
            checked={excludeLowArticles}
            onChange={(e) => handleExcludeLowArticlesChange(e.target.checked)}
            className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer row-span-1"
          />
          <label htmlFor={lowArticlesId} className="font-medium text-base text-black cursor-pointer self-center">
            Exclude 'low article' companies from emails
          </label>
          <div />
        </div>
      </div>

      {mode === 'create' && (
        <div className="flex flex-row gap-2 mt-1">
          <button
            onClick={handleSaveClick}
            disabled={!name.trim() || (!scoreIncreaseAlertEnabled && !scoreDecreaseAlertEnabled)}
            className="bg-ow-blue text-white font-medium px-6 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            Save
          </button>
          {onCancel && (
            <button onClick={onCancel} className="bg-gray-300 text-black font-medium px-6 py-2 rounded hover:bg-gray-400 transition-colors">
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCustomAlertEditor;
